import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getClientConfig } from "../../lib/apiClient";
import { useStore } from "../../stores/rootStore";

let gsiPromise: Promise<void> | null = null;
function loadGsi(): Promise<void> {
	if (gsiPromise) return gsiPromise;
	gsiPromise = new Promise<void>((resolve, reject) => {
		if (window.google?.accounts?.id) {
			resolve();
			return;
		}
		const script = document.createElement("script");
		script.src = "https://accounts.google.com/gsi/client";
		script.async = true;
		script.defer = true;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error("Failed to load Google Identity Services"));
		document.head.appendChild(script);
	});
	return gsiPromise;
}

/**
 * Google's GIS iframe remains the click target, while the visual layer matches the Apple
 * provider button. GIS owns its iframe styles, so styling the iframe directly would leave the
 * two providers visibly different.
 */
export function GoogleSignInButton() {
	const { auth } = useStore();
	const { i18n, t } = useTranslation();
	const config = getClientConfig();
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!config.googleClientId) return;
		let cancelled = false;
		loadGsi()
			.then(() => {
				if (cancelled || !containerRef.current || !window.google?.accounts?.id) return;
				containerRef.current.replaceChildren();
				window.google.accounts.id.initialize({
					client_id: config.googleClientId,
					callback: (response) => {
						if (response.credential) void auth.signInWithGoogle(response.credential);
					},
				});
				window.google.accounts.id.renderButton(containerRef.current, {
					theme: "filled_black",
					size: "large",
					text: "continue_with",
					shape: "rectangular",
					width: 400,
					locale: i18n.language.startsWith("zh") ? "zh-CN" : "en",
				});
				// Keep the transparent GIS target aligned with the custom 40px visual button.
				const iframe = containerRef.current.querySelector("iframe");
				if (iframe) {
					iframe.style.width = "100%";
					iframe.style.height = "100%";
				}
			})
			.catch(() => {
				/* blocked/offline — other methods remain */
			});
		return () => {
			cancelled = true;
		};
	}, [config.googleClientId, auth, i18n.language]);

	if (!config.googleClientId) return null;
	return (
		<div className="auth-provider-shell relative h-10 w-full overflow-hidden rounded-lg">
			<div className="auth-provider-button pointer-events-none absolute inset-0">
				<span aria-hidden="true" className="text-[1.15rem] font-medium leading-none">
					G
				</span>
				<span>{t("auth.googleContinue")}</span>
			</div>
			<div
				ref={containerRef}
				aria-label={t("auth.googleContinue")}
				className="absolute inset-0 z-10 flex h-10 w-full justify-center opacity-0"
			/>
		</div>
	);
}
